import type { EnvironmentProviders, Provider } from '@angular/core';
import { importProvidersFrom, inject, provideEnvironmentInitializer } from '@angular/core';
import type { Routes } from '@angular/router';
import { Router } from '@angular/router';

import { Actions, ofType, provideEffects } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge, take, tap } from 'rxjs';

import { UserActions } from '@core/account-data';
import { BulkActionsController, BulkActionsSelectionTracker } from '@core/bulk-actions-legacy';
import { ViewsOffer } from '@core/offers-types';
import { RoleActions } from '@core/roles-data';
import { DataSourceActions } from '@core/views-data';
import { WrappedTableListGroupHeadingComponent } from '@core/views-ui';
import type { TableConfig } from '@core/views-util';
import {
  dataSourceProviders,
  provideBoardLayout,
  provideListLayout,
  provideTableLayout,
  provideViewBaseRoute,
} from '@core/views-util';
import { provideSlotsLimitDialogOpenerContract } from '@recruitee/billing-contracts';
import { SlotsLimitDialogOpenerService } from '@recruitee/billing-smart-ui';
import { JobsAnalytics } from '@recruitee/offers-legacy';
import { JobActionsFacade } from '@recruitee/offers-logic';

// eslint-disable-next-line boundaries/element-types
import { CandidatesDataSourceService } from '../../../../recruitee/src/app/features/candidate/views/candidates-data-source.service';
// eslint-disable-next-line boundaries/element-types
import { JobsBoardCardComponent } from '../../../../recruitee/src/app/features/offers/views/board/card/jobs-board-card.component';
// eslint-disable-next-line boundaries/element-types
import { JobsGroupHeaderComponent } from '../../../../recruitee/src/app/features/offers/views/board/group-header/group-header.component';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsManageEeoBulkService } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-manage-eeo-bulk.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsManageReferralsBulkService } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-manage-referrals-bulk.service';
// eslint-disable-next-line boundaries/element-types
import { ViewsOffersBulkActionsController } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions-controller.service';
// eslint-disable-next-line boundaries/element-types
import { ViewsOffersBulkActionsExecutorService } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions-executor.service';
// eslint-disable-next-line boundaries/element-types
import { ViewsOffersBulkActionsValidatorService } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions-validator.service';
// eslint-disable-next-line boundaries/element-types
import { OfferListWrapperComponent } from '../../../../recruitee/src/app/features/offers/views/offer-list-item/offer-list-wrapper.component';
// eslint-disable-next-line boundaries/element-types
import { JobsDataSourceService } from '../../../../recruitee/src/app/features/offers/views/services/jobs-data-source.service';
// eslint-disable-next-line boundaries/element-types
import { ViewsJobsEffects } from '../../../../recruitee/src/app/features/offers/views/services/jobs.effects';
import {
  offerColumnFieldMap,
  offerColumns,
  // eslint-disable-next-line boundaries/element-types
} from '../../../../recruitee/src/app/features/offers/views/table-cells/offer-columns';
// eslint-disable-next-line boundaries/element-types
import type { OfferTableFields } from '../../../../recruitee/src/app/features/offers/views/table-cells/offer-columns';
// eslint-disable-next-line boundaries/element-types
import { RtPricingFeatureConnectedModule } from '../../../../recruitee/src/app/features/pricing/connected/pricing-feature-connected.module';
import { NewDialogComponent } from '../new-dialog/new-dialog.component';

const viewsEnvInitialize: Array<Provider | EnvironmentProviders> = [
  provideEnvironmentInitializer(() => {
    const initializerFn = (() => {
      const store = inject(Store);
      const actions$ = inject(Actions);
      const router = inject(Router);

      merge(
        actions$.pipe(
          ofType(DataSourceActions.fetchOneFailed),
          tap(() => router.navigate(['/'])),
        ),
        actions$.pipe(ofType(DataSourceActions.fetchOneSuccess)),
      )
        .pipe(take(1))
        .subscribe();

      return () => {
        store.dispatch(UserActions.fetchCollection());
        store.dispatch(RoleActions.fetchCollection());
      };
    })();
    return initializerFn();
  }),
];

const viewsProviders: Array<Provider | EnvironmentProviders> = [
  CandidatesDataSourceService,
  JobsDataSourceService,
  provideEffects(ViewsJobsEffects),
  dataSourceProviders('jobs', {
    jobs: JobsDataSourceService,
    candidates: CandidatesDataSourceService,
  }),
  provideViewBaseRoute('/views'),

  // To make offer card work
  // { provide: OfferAnalytics, useClass: JobsAnalytics },
  JobActionsFacade,
  provideSlotsLimitDialogOpenerContract({ useClass: SlotsLimitDialogOpenerService }),
  JobsAnalytics,
  provideListLayout({
    jobs: { header: WrappedTableListGroupHeadingComponent, record: OfferListWrapperComponent },
  }),
  provideTableLayout({
    jobs: <TableConfig<ViewsOffer, OfferTableFields>>{
      tableGroupHeader: WrappedTableListGroupHeadingComponent,
      columns: offerColumns,
      columnFieldMap: offerColumnFieldMap,
    },
  }),
  provideBoardLayout({
    jobs: {
      item: JobsBoardCardComponent,
      groupHeader: JobsGroupHeaderComponent,
    },
  }),
  importProvidersFrom(RtPricingFeatureConnectedModule),
  // Offer bulk actions services
  ViewsOffersBulkActionsController,
  { provide: BulkActionsController, useExisting: ViewsOffersBulkActionsController },
  ViewsOffersBulkActionsExecutorService,
  ViewsOffersBulkActionsValidatorService,
  ViewsManageEeoBulkService,
  ViewsManageReferralsBulkService,
  BulkActionsSelectionTracker,
];

export const ROUTES: Routes = [
  {
    path: 'prosemirror',
    loadChildren: () => import('../prosemirror/prosemirror.module').then(m => m.ProsemirrorModule),
  },
  {
    path: 'views',
    loadChildren: () =>
      import('@core/views-legacy').then(m =>
        m.provideDataSourceRouting([viewsEnvInitialize, viewsProviders], true),
      ),
  },
  {
    path: 'analytics',
    loadChildren: () => import('@recruitee/reports-smart-ui').then(m => m.REPORTS_ROUTES),
  },
  {
    path: 'job-dashboard',
    loadChildren: () =>
      import('../reports-new-job-dashboard/reports-new-job-dashboard.routing').then(
        m => m.newJobDashboardRoutes,
      ),
  },
  {
    path: 'reports-generic-grid',
    loadChildren: () =>
      import('../reports-dashboard-grid-generic/reports-dashboard-grid-generic.routing').then(
        m => m.reportsDashboardGridGenericRouting,
      ),
  },
  {
    path: 'dialog',
    component: NewDialogComponent,
  },
  {
    path: 'icons',
    loadChildren: () => import('../icons/icons.component').then(m => m.ROUTES),
  },
];
